import * as Sentry from '@sentry/react';
import type { Span } from '@sentry/react';
import { type LoaderKind, loaderPresence } from '@vrplatform/components';
import {
  SENTRY_PORTAL_STORAGE_KEY,
  useQueryClient,
  useTeam,
  useWriteSpans,
  waitUntilQuiet,
} from '@vrplatform/data';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const LOADER_KINDS: readonly LoaderKind[] = ['full-page', 'inline'];

/**
 * Records how long each route takes until its content shows. `view.ready`
 * starts with the page load or navigation and ends when no query is still on
 * its first fetch and no full-page loader shows, for 150 ms. Loaders that show
 * while the route is loading get a `ui.loader` span each. A route that is
 * still loading after 20 s ends with status `deadline_exceeded`, one that is
 * left first with `cancelled`, and one that shows in time with `ok`. A redirect
 * that replaces the URL keeps the page load's span running, so a cold load that
 * redirects reports one `view.ready` from page start to the final route.
 * Mount it once inside the Sentry router, for one portal.
 */
export function ViewReadySpan({ portal }: { portal: 'pm' | 'owner' }) {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [{ id: teamId }] = useTeam();
  const stop = useRef<{ parent: Span; abort: () => void }>(undefined);

  useWriteSpans();

  useEffect(() => {
    const parent = Sentry.getActiveSpan();
    if (!parent) return;
    // A redirect that replaces the URL changes the path but not the page
    // load or navigation span, and its view keeps running.
    if (stop.current?.parent === parent) return;
    stop.current?.abort();

    const attributes = { 'team.id': teamId, portal };
    const view = Sentry.startInactiveSpan({
      name: 'view.ready',
      op: 'ui.view.ready',
      parentSpan: parent,
      // The page load or navigation started before this mounted; boot, auth
      // and the route chunk are part of the wait.
      startTime: Sentry.spanToJSON(parent).start_timestamp,
      attributes,
    });
    const loaderSpans = new Map<LoaderKind, Span>();
    const syncLoaderSpans = () => {
      for (const kind of LOADER_KINDS) {
        const open = loaderSpans.get(kind);
        if (loaderPresence.count(kind) > 0 && !open) {
          loaderSpans.set(
            kind,
            Sentry.startInactiveSpan({
              name: 'loader',
              op: 'ui.loader',
              parentSpan: view,
              attributes: { ...attributes, 'loader.kind': kind },
            })
          );
        } else if (loaderPresence.count(kind) === 0 && open) {
          open.setStatus({ code: 1 });
          open.end();
          loaderSpans.delete(kind);
        }
      }
    };
    const stopLoaderSpans = loaderPresence.subscribe(syncLoaderSpans);
    syncLoaderSpans();

    const controller = new AbortController();
    stop.current = { parent, abort: () => controller.abort() };
    waitUntilQuiet({
      isBusy: () =>
        loaderPresence.count('full-page') > 0 ||
        queryClient.isFetching({
          predicate: (query) => query.state.data === undefined,
        }) > 0,
      quietMs: 150,
      signal: controller.signal,
      subscribe: (listener) => {
        const stopQueries = queryClient.getQueryCache().subscribe(listener);
        const stopLoaders = loaderPresence.subscribe(listener);
        return () => {
          stopQueries();
          stopLoaders();
        };
      },
      timeoutMs: 20_000,
    }).then((result) => {
      stopLoaderSpans();
      const status: Parameters<Span['setStatus']>[0] =
        result === 'timeout'
          ? { code: 2, message: 'deadline_exceeded' }
          : result === 'aborted'
            ? { code: 2, message: 'cancelled' }
            : { code: 1 };
      for (const loaderSpan of loaderSpans.values()) {
        loaderSpan.setStatus(status);
        loaderSpan.end();
      }
      view.setStatus(status);
      view.end();
    });
  }, [pathname]);

  useEffect(() => () => stop.current?.abort(), []);

  // The next page load of this browser samples every owner portal trace.
  useEffect(() => {
    globalThis.localStorage?.setItem(SENTRY_PORTAL_STORAGE_KEY, portal);
  }, [portal]);

  return null;
}
